import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotificationSettings, updateNotificationSettings } from '../../redux/thunk/notificationsThunk';
import './NotificationsTab.scss';

const NOTIFICATION_TYPES = [
    {
        appKey: 'likedTrackPlayed',
        emailKey: 'emailLikedTrackPlayed',
        title: 'Liked track played',
        description: 'When a liked track with the bell on appears in a new mix.',
    },
    {
        appKey: 'favouriteArtistNewMix',
        emailKey: 'emailFavouriteArtistNewMix',
        title: 'Followed artist — new mix',
        description: 'When a mix by a followed artist with the bell on is added to Mixpedia.',
        hasWindowSelect: true,
    },
    {
        appKey: 'favouriteArtistTrackPlayed',
        emailKey: 'emailFavouriteArtistTrackPlayed',
        title: 'Followed artist — track played',
        description: 'When a track by a bell-enabled artist appears in a mix.',
    },
];

// Recency window for the followed-artist new-mix type. null = any mix (the
// default — old sets still notify, just with "was added" copy instead of
// "new"). Values map to user_notification_settings.new_mix_window_days.
export const NEW_MIX_WINDOW_OPTIONS = [
    { value: null, label: 'Any mix' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 3 months' },
    { value: 180, label: 'Last 6 months' },
    { value: 365, label: 'Last year' },
];

const Toggle = ({ on, onClick, disabled }) => (
    <button
        type="button"
        className={`notif-toggle ${on ? 'on' : 'off'}`}
        onClick={onClick}
        disabled={disabled}
        aria-pressed={on}
    >
        <span className="notif-toggle-knob" />
    </button>
);

const NotificationsTab = () => {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const { settings, settingsLoading } = useSelector((state) => state.notifications);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchNotificationSettings());
        }
    }, [isAuthenticated, dispatch]);

    const handleToggle = (key) => {
        dispatch(updateNotificationSettings({ ...settings, [key]: !settings[key] }));
    };

    const handleWindowChange = (e) => {
        const v = e.target.value;
        dispatch(updateNotificationSettings({
            ...settings,
            newMixWindowDays: v === '' ? null : Number(v),
        }));
    };

    if (!isAuthenticated) {
        return (
            <div className="notifications-tab">
                <div className="notif-container">
                    <h2 className="notif-page-title">Notifications</h2>
                    <p className="notif-empty">Sign in to manage notification preferences.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="notifications-tab">
            <div className="notif-container">
                <h2 className="notif-page-title">Notifications</h2>
                <p className="notif-page-subtitle">
                    Choose what you want to hear about — in the app, by email, or both.
                </p>

                {settingsLoading && !settings && (
                    <div className="notif-loading">
                        <div className="notif-spinner" />
                    </div>
                )}

                <div className="notif-card">
                    <div className="notif-row notif-header-row">
                        <div className="notif-row-info" />
                        <div className="notif-row-toggles">
                            <span className="notif-col-label">In-app</span>
                            <span className="notif-col-label">Email</span>
                        </div>
                    </div>

                    {NOTIFICATION_TYPES.map(({ appKey, emailKey, title, description, hasWindowSelect }) => (
                        <div key={appKey} className="notif-row">
                            <div className="notif-row-info">
                                <span className="notif-row-title">{title}</span>
                                <span className="notif-row-desc">{description}</span>
                                {hasWindowSelect && (
                                    <label className="notif-window">
                                        <span className="notif-window-label">Mixes from</span>
                                        <select
                                            className="notif-window-select"
                                            value={settings.newMixWindowDays ?? ''}
                                            onChange={handleWindowChange}
                                            disabled={settingsLoading || !settings[appKey]}
                                        >
                                            {NEW_MIX_WINDOW_OPTIONS.map(({ value, label }) => (
                                                <option key={label} value={value ?? ''}>{label}</option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                            </div>
                            <div className="notif-row-toggles">
                                <Toggle
                                    on={!!settings[appKey]}
                                    onClick={() => handleToggle(appKey)}
                                    disabled={settingsLoading}
                                />
                                <Toggle
                                    on={!!settings[emailKey]}
                                    onClick={() => handleToggle(emailKey)}
                                    disabled={settingsLoading}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <p className="notif-page-footnote">
                    Bells live next to the Follow and Like buttons — turn them on per artist or track.
                </p>
            </div>
        </div>
    );
};

export default NotificationsTab;
